import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Radio, Sparkles, Zap, Lock } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useSmoothScroll } from "./SmoothScroll";

const ease = [0.16, 1, 0.3, 1];
const HERO_IMG = "https://images.pexels.com/photos/39570848/pexels-photo-39570848.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const MaskedLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay: 0.25 + i * 0.16, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export const KineticHero = () => {
  const { t } = useLang();
  const { scrollTo } = useSmoothScroll();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const px = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 90, damping: 20 });
  const py = useSpring(useTransform(my, [-0.5, 0.5], [-10, 10]), { stiffness: 90, damping: 20 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <section data-testid="hero-section" className="relative mx-auto max-w-7xl px-4 pt-24 sm:px-6 lg:px-8 lg:pt-28">
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        initial={{ opacity: 0, scale: 0.985 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.1, ease }}
        className="hero-card grain relative overflow-hidden rounded-[2rem] border border-white/70 shadow-[0_40px_90px_-50px_rgba(76,29,149,0.45)]"
      >
        <motion.div
          style={{ x: px, y: py }}
          className="pointer-events-none absolute -right-24 -top-24 h-[28rem] w-[28rem] rounded-full bg-gradient-to-br from-violet-300/40 to-pink-300/30 blur-3xl"
        />
        <div className="relative grid gap-10 p-6 sm:p-10 lg:grid-cols-12 lg:gap-8 lg:p-14">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1, ease }} className="flex flex-wrap items-center gap-2">
              <span data-testid="hero-18plus-badge" className="inline-flex items-center rounded-full border-2 border-ink px-2.5 py-0.5 font-display text-xs font-extrabold text-ink">18+</span>
              <span data-testid="hero-coming-soon-badge" className="eyebrow inline-flex items-center gap-1.5 rounded-full bg-ink px-3 py-1 text-white">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-pink-400" />
                {t.hero.comingSoon}
              </span>
              <span className="eyebrow inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-white/70 px-3 py-1 text-violet-700">
                <Sparkles size={11} /> {t.hero.eyebrow}
              </span>
            </motion.div>

            <h1 data-testid="hero-headline" className="font-display mt-8 text-[2.75rem] font-extrabold leading-[0.98] tracking-[-0.03em] text-ink sm:text-6xl lg:text-[5.25rem]">
              <MaskedLine i={0}>{t.hero.lines[0]}</MaskedLine>
              <MaskedLine i={1} className="text-gradient">{t.hero.lines[1]}</MaskedLine>
            </h1>

            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7, ease }} className="mt-7 max-w-xl text-base leading-relaxed text-ink-2 sm:text-lg">
              {t.hero.sub}
            </motion.p>
            <motion.p data-testid="hero-definition" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8, ease }} className="mt-4 max-w-xl border-l-2 border-violet-500 pl-4 text-sm font-medium leading-relaxed text-ink">
              {t.hero.definition}
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85, ease }} className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/discover" data-testid="hero-discover-cta" className="pill btn-primary inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold">
                <Zap size={16} /> {t.hero.ctaFans}
              </Link>
              <button onClick={() => scrollTo("#waitlist")} data-testid="hero-waitlist-cta" className="pill btn-ghost inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold">
                {t.hero.ctaCreators}
              </button>
            </motion.div>

            <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.1 }} className="mt-10 flex flex-wrap gap-x-5 gap-y-2">
              {t.hero.steps.map((s, i) => (
                <li key={s} className="eyebrow flex items-center gap-1.5 text-[0.62rem] text-ink-2">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: i % 2 ? "#ec4899" : "#7c3aed" }} />
                  {s}
                </li>
              ))}
            </motion.ul>
          </div>

          <motion.div
            style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease }}
            className="relative flex flex-col gap-4 lg:col-span-5"
          >
            <div className="card-soft overflow-hidden">
              <div className="relative h-44 bg-gradient-to-br from-violet-200 via-fuchsia-100 to-pink-200">
                <img src={HERO_IMG} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                <span className="eyebrow absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-[0.6rem] text-ink">
                  <Radio size={11} className="text-pink-500" /> {t.hero.card1Label}
                </span>
              </div>
              <div className="p-4">
                <p className="text-xs text-mist">{t.hero.card1Creator}</p>
                <p className="font-display mt-1 text-lg font-bold leading-tight text-ink">{t.hero.card1Title}</p>
              </div>
            </div>
            <div className="card-soft flex items-center gap-4 p-4">
              <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-night">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,#7c3aed_0%,transparent_55%),radial-gradient(circle_at_80%_80%,#ec4899_0%,transparent_50%)] opacity-80" />
                <Lock size={16} className="absolute bottom-2 right-2 text-white/80" />
              </div>
              <div className="min-w-0">
                <span className="eyebrow inline-flex items-center gap-1.5 rounded-full bg-pink-100 px-2 py-0.5 text-[0.58rem] text-pink-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-pink-500" /> {t.hero.card2Label}
                </span>
                <p className="font-display mt-1.5 truncate text-base font-bold text-ink">{t.hero.card2Title}</p>
                <p className="text-xs text-mist">{t.hero.card2Meta}</p>
              </div>
            </div>
            <p className="eyebrow text-right text-[0.58rem] text-mist">{t.hero.operated}</p>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        data-testid="hero-development-notice"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.8 }}
        className="mt-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      >
        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber-500" />
        <p>{t.hero.devNotice}</p>
      </motion.div>
    </section>
  );
};
